import React, { useMemo } from "react";

import { DateTime } from "luxon";
import { useNavigate } from "react-router-dom";

import Card from "../Shared/Card/Card";
import LoadingWheel from "../Shared/LoadingWheel";
import useProperties from "../../data/useProperties";
import formatDateTime from "../../utils/formatDateTime";
import useAgencyBranches from "../../data/useAgencyBranches";
import { useViewingsByPerson } from "../../data/viewing/useViewingsByPerson";
import { useFormDataByPerson } from "../../data/formData/useFormDataByPerson";
import { useMortgagesByPerson } from "../../data/mortgage/useMortgagesByPerson";
import { useValuationsByPerson } from "../../data/valuation/useValuationByPerson";
import { useAgencyEnquiresByPerson } from "../../data/agency/useAgencyEnquiresByPerson";
import { formLeadStatusEnum, mortgageStatusEnum, valuationStatusEnum, viewingStatusEnum } from "../../constants/enums";
import ActionButtonContainer from "../Shared/ActionButtonContainer";
import ActionButton from "../Shared/ActionButton";
import { EyeIcon } from "@heroicons/react/outline";

enum EnquiryType {
  Valuation,
  Contact,
  Registration,
  Mortgage,
  Viewing,
  Form
}

interface Enquiry {
  id: string,
  type: EnquiryType,
  branchLookupId: string,
  status?: string,
  created: string
}

interface PersonValuationsProps {
  personId: string;
  agencyId: string;
}

interface EnquiryRowProps {
  agencyId: string;
  enquiry: Enquiry;
}

function enquiryTypeToString(type: EnquiryType): string {
  switch (type) {
    case EnquiryType.Valuation:
      return "Valuation";
    case EnquiryType.Contact:
      return "Contact Form";
    case EnquiryType.Registration:
      return "Registration";
    case EnquiryType.Mortgage:
      return "Mortgage";
    case EnquiryType.Viewing:
      return "Viewing";
    case EnquiryType.Form:
      return "Form";
  }
}

function getEnquiryNavigationUrl(type: EnquiryType): string {
  switch (type) {
    case EnquiryType.Valuation:
      return "../../valuations/valuation";
    case EnquiryType.Contact:
      return "../../enquiries/enquiry";
    case EnquiryType.Registration:
      return "../../registrations/registration";
    case EnquiryType.Mortgage:
      return "../../mortgages/mortgage";
    case EnquiryType.Viewing:
      return "../../viewings/viewing";
    case EnquiryType.Form:
      return "../../formleads/formlead";
    default:
      return "";
  }
}

function EnquiryRow({ enquiry, agencyId }: EnquiryRowProps) {
  const navigate = useNavigate();
  const { branches } = useAgencyBranches(agencyId);
  const { properties } = useProperties();


  const getBranchName = (lookupId: string) => {
    if (enquiry.type === EnquiryType.Viewing) {
      const property = properties.data?.find(p => p.id === lookupId);

      if (property) {
        lookupId = property.branchId;
      }
    }

    // @ts-ignore
    const branch = branches.data?.find(b => b.id === lookupId);
    return branch ? branch.name : "--";
  }

  return (
    <tr onClick={() => navigate(`${getEnquiryNavigationUrl(enquiry.type)}/${enquiry.id}`)} className="hover:bg-gray-50 cursor-pointer">
      <td className="py-4 px-3 sm:pl-6 text-left text-sm">{enquiryTypeToString(enquiry.type)}</td>
      <td className="py-4 px-3 text-center text-sm">{enquiry.status}</td>
      <td className="py-4 px-3 text-center text-sm">{getBranchName(enquiry.branchLookupId)}</td>
      <td className="py-4 px-3 text-center text-sm">{formatDateTime(enquiry.created, DateTime.DATETIME_SHORT)}</td>
      <td className="py-4 px-3 text-center text-sm">
        <ActionButtonContainer>
          <ActionButton
            onClick={() => navigate(`${getEnquiryNavigationUrl(enquiry.type)}/${enquiry.id}`)}
            tooltip="Edit"
            Icon={EyeIcon} />
        </ActionButtonContainer>
      </td>
    </tr>
  );
}

export function PersonEnquiries({ personId, agencyId }: PersonValuationsProps) {
  const { branches } = useAgencyBranches(agencyId);
  const { valuations } = useValuationsByPerson(agencyId, personId);
  const { mortgages } = useMortgagesByPerson(agencyId, personId);
  const { viewings } = useViewingsByPerson(agencyId, personId);
  const { formData } = useFormDataByPerson(agencyId, personId);
  const { enquiries } = useAgencyEnquiresByPerson(agencyId, personId);
  const [loading, setLoading] = React.useState(true);

  const rows = useMemo(() => {
    if (!branches.isLoading && !valuations.isLoading && !mortgages.isLoading && !viewings.isLoading && !formData.isLoading && !enquiries.isLoading) {
      let rows = [
        // @ts-ignore
        ...valuations.data?.data.map(v => ({ id: v.id, type: EnquiryType.Valuation, branchLookupId: v.branchId, status: valuationStatusEnum[v.status], created: v.created })),
        // @ts-ignore
        ...mortgages.data?.data.map(m => ({ id: m.id, type: EnquiryType.Mortgage, branchLookupId: m.branchId, status: mortgageStatusEnum[m.status], created: m.created })),
        // @ts-ignore
        ...viewings.data?.data.map(vw => ({ id: vw.id, type: EnquiryType.Viewing, branchLookupId: vw.propertyId, status: viewingStatusEnum[vw.status], created: vw.created })),
        // @ts-ignore
        ...formData.data?.data.map(fd => ({ id: fd.id, type: EnquiryType.Form, branchLookupId: fd.branchId, status: formLeadStatusEnum[fd.status], created: fd.created })),
        // @ts-ignore
        ...enquiries.data?.data.map(eq => ({ id: eq.id, type: EnquiryType.Contact, branchLookupId: eq.branchId, status: undefined, created: eq.created })),
      ]

      setLoading(false);
      return rows;
    } else {
      return [];
    }
  }, [
    valuations.isLoading,
    mortgages.isLoading,
    viewings.isLoading,
    formData.isLoading,
    enquiries.isLoading
  ]);



  return (
    // @ts-ignore
    <Card title="Enquiries" containerClassName="h-full" bodyClassName="h-full">
      {loading ? (
        <div className="flex items-center justify-center py-10">
          <LoadingWheel width="w-10" height="h-10" />
        </div>
      ) : (
        <div className="px-6 pb-6">
          <div className="mt-6 overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:-mx-6 md:mx-0 md:rounded-lg">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="py-3.5 !pl-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Enquiry Type</th>
                  <th scope="col" className="py-3.5 !pl-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Status</th>
                  <th scope="col" className="py-3.5 !pl-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Branch</th>
                  <th scope="col" className="py-3.5 !pl-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Created On</th>
                  <th scope="col" className="py-3.5 !pl-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-sm italic text-gray-500">No enquiries found.</td>
                  </tr>
                ) : (
                  rows.map((enquiry: Enquiry) => (
                    <EnquiryRow
                      key={enquiry.id}
                      enquiry={enquiry}
                      agencyId={agencyId}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}